import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

const projects = [
  {
    id: 1,
    title: 'Asset Management System',
    description: 'A comprehensive solution for tracking and managing organizational assets throughout their lifecycle, including procurement requests, issue tracking, returns, AMC complaints, and consumables management.',
    image: '/images/portfolio/asset-management.svg',
    technologies: ['React', 'Node.js', 'MongoDB', 'Material-UI', 'Express'],
    challenge: 'Creating a robust system to efficiently manage the complete lifecycle of company assets, from procurement to retirement, while ensuring accurate tracking and maintenance. The client needed a solution to replace manual tracking and paper-based workflows with a digital system that would provide real-time visibility into asset status.',
    solution: 'Implemented a modular architecture with role-based access control, barcode scanning integration, and automated notification system for maintenance alerts and approvals. The system includes specialized modules for request management, asset issuance, returns processing, AMC complaint tracking, and consumables inventory management.',
    results: '60% reduction in asset loss, 40% improvement in maintenance response time, and complete elimination of manual paperwork. The system successfully manages over 5,000 assets and processes approximately 200 requests monthly.',
    features: [
      'User request management for new assets and consumables',
      'Asset issuance tracking with digital signatures',
      'Return processing and condition assessment',
      'AMC complaint logging and resolution tracking',
      'Consumables inventory and usage monitoring',
      'Comprehensive reporting and analytics dashboard',
      'Automated email notifications for request status',
      'Asset lifecycle tracking from procurement to retirement'
    ],
    status: 'Completed'
  },
  {
    id: 2,
    title: 'Healthcare Management System',
    description: 'Digital healthcare platform for patient management and telemedicine services.',
    image: '/images/portfolio/healthcare.svg',
    technologies: ['React Native', 'Express.js', 'PostgreSQL', 'Socket.io'],
    challenge: 'Developing a secure platform that meets HIPAA compliance while providing seamless telemedicine capabilities.',
    solution: 'Planning to implement end-to-end encryption, secure video conferencing, and electronic health records management.',
    results: 'Expected to serve thousands of patients with zero security incidents.',
    status: 'Coming Soon'
  },
  {
    id: 3,
    title: 'E-Commerce Platform',
    description: 'A full-featured e-commerce solution with inventory management and payment processing.',
    image: '/images/portfolio/ecommerce.svg',
    technologies: ['React', 'Node.js', 'MongoDB', 'Stripe'],
    challenge: 'Building a scalable platform that can handle high transaction volumes with real-time inventory updates.',
    solution: 'Planning to use microservices architecture with Redis caching for optimal performance.',
    results: 'Expected to achieve significant improvements in transaction speed and system uptime.',
    status: 'Coming Soon'
  }
];

const Portfolio = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [open, setOpen] = useState(false);

  const handleClickOpen = (project) => {
    setSelectedProject(project);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6, mb: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
            Our Portfolio
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 800 }}>
            Discover how we've helped businesses transform their digital presence
          </Typography>
        </Container>
      </Box>

      {/* Projects Grid */}
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {projects.map((project) => (
            <Grid item xs={12} md={6} key={project.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                  position: 'relative',
                }}
                elevation={2}
              >
                {project.status === 'Coming Soon' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 0,
                      bgcolor: 'secondary.main',
                      color: 'white',
                      py: 1,
                      px: 2,
                      borderTopLeftRadius: 4,
                      borderBottomLeftRadius: 4,
                      zIndex: 1,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {project.status}
                    </Typography>
                  </Box>
                )}
                <CardMedia
                  component="img"
                  height="240"
                  image={project.image}
                  alt={project.title}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {project.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph align="justify">
                    {project.description}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {project.technologies.map((tech) => (
                      <Chip
                        key={tech}
                        label={tech}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleClickOpen(project)}
                  >
                    View Details
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Project Details Dialog */}
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
        >
          {selectedProject && (
            <>
              <DialogTitle>
                <Typography variant="h4" component="h2">
                  {selectedProject.title}
                </Typography>
              </DialogTitle>
              <DialogContent dividers>
                <Box sx={{ mb: 3 }}>
                  <img
                    src={selectedProject.image}
                    alt={selectedProject.title}
                    style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                  />
                </Box>
                {selectedProject.status === 'Coming Soon' ? (
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h6" color="secondary.main" gutterBottom>
                      Project in Development
                    </Typography>
                    <Typography paragraph align="justify">
                      This project is currently in our development pipeline. We're excited to bring this solution to our clients soon!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Challenge
                    </Typography>
                    <Typography paragraph align="justify">
                      {selectedProject.challenge}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      Solution
                    </Typography>
                    <Typography paragraph align="justify">
                      {selectedProject.solution}
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      Results
                    </Typography>
                    <Typography paragraph align="justify">
                      {selectedProject.results}
                    </Typography>
                    {selectedProject.features && (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Key Features
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                          {selectedProject.features.map((feature, index) => (
                            <Typography key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box component="span" sx={{ minWidth: '8px', height: '8px', borderRadius: '50%', bgcolor: 'primary.main', mr: 2, display: 'inline-block' }} />
                              {feature}
                            </Typography>
                          ))}
                        </Box>
                      </>
                    )}
                  </>
                )}
                <Typography variant="h6" gutterBottom>
                  Technologies Used
                </Typography>
                <Box>
                  {selectedProject.technologies.map((tech) => (
                    <Chip
                      key={tech}
                      label={tech}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
};

export default Portfolio; 